import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { MecImage } from '@adapters/bigcommerce/types/product';
import { buildPdpImageGalleryOrder } from '@utils/buildPdpImageGalleryOrder';
import CanadianBadge from '_components/UI/CanadianBadge';
import ExpandButton from '_components/UI/ExpandButton';
import PlaceholderImage from './PlaceholderImage';
import GalleryTrack from './components/GalleryTrack';
import MobileScrollbar from './components/MobileScrollbar';
import { FIRST_IMAGE_SIZES, GalleryImageFitMode } from './constants';
import { getGalleryCoverRangeOverride } from './qaOverrides';
import useMobileCarousel from './hooks/useMobileCarousel';
import useNarrowGalleryViewport from './hooks/useNarrowGalleryViewport';
import styles from './ImageGallery.module.scss';

const ZoomModal = dynamic(() => import('./components/ZoomModal'), {
  ssr: false,
});

/** Stable fallback so `useMemo` deps do not change every render when `images` is omitted. */
const EMPTY_IMAGES: MecImage[] = [];

export interface ImageGalleryProps {
  images?: MecImage[];
  isMadeInCanada?: boolean;
  isCanadianBrand?: boolean;
  initialImageCount?: number;
  colourFilterName?: string | null;
  /**
   * Forces object-fit for gallery grid images to `cover` or `contain`, bypassing type-based
   * fit (PRI/ALT contain, LFS cover). Zoom modal hero is always `contain`; zoom thumbs follow image type.
   */
  imageFitMode?: GalleryImageFitMode;
}

/**
 * ImageGallery displays product images in a responsive grid layout.
 *
 * Features:
 * - Responsive grid: 1 column mobile, 2 columns desktop
 * - First image spans 2 columns on desktop (hero)
 * - CanadianBadge overlay on first image (when applicable)
 * - Expand/collapse functionality for large image sets
 * - Optimized image loading (priority for first image)
 * - Placeholder when the ordered list is empty (no images, or colour filter with no matching PRI/ALT)
 *
 * Layout:
 * - Mobile: Square aspect ratio (1:1), single column
 * - Desktop: First image square (2 cols), remaining portrait (423:564)
 * - Desktop expand: shows up to `initialImageCount` cells first (default **7**), then "View all" for the rest **of the ordered list** (mobile always shows the full ordered strip). The zoom modal always uses the full ordered list, not the collapsed grid slice.
 * - Ordered gallery (PDP): at most **10** images after {@link buildPdpImageGalleryOrder} (1 PRI, 7 ALT, 2 LFS); untyped non-PRI/ALT non-LFS rows are omitted.
 * - If the selected colour has no ALT angles (with or without a hero), ALT slots are filled from the other swatch with the most ALTs; labels use `product.colour` + donor swatch (see `getGalleryImageTagDisplay`).
 *
 * @param images - Array of product images to display
 * @param isMadeInCanada - Show made-in-Canada (black leaf) on first image when true
 * @param initialImageCount - Desktop-only: max cells before expand (default **7**). Independent of the PDP ordered cap (10).
 * @param colourFilterName - Optional swatch name to restrict visible images (PDP), including
 *   `NO_COLOUR` for PRI/ALT. Product-scoped `LFS` (empty pipe colour or `NO_COLOUR|…`) is included
 *   for every colour variant whenever that variant has PRI/ALT; variant `NO_COLOUR` is not used
 *   as an LFS colour key. If nothing matches the swatch or there is no PRI/ALT for that swatch,
 *   the gallery shows the placeholder (ordered list is empty).
 */
const ImageGallery: FC<ImageGalleryProps> = ({
  images,
  isMadeInCanada = false,
  isCanadianBrand = false,
  initialImageCount = 7,
  colourFilterName = null,
  imageFitMode,
}) => {
  const { t } = useTranslation('pdp');
  const [isExpanded, setIsExpanded] = useState(false);
  const [coverRangeOverride, setCoverRangeOverride] = useState<
    number | undefined
  >(undefined);
  const isNarrowViewport = useNarrowGalleryViewport();
  const [zoomState, setZoomState] = useState({ isVisible: false, slideNum: 0 });
  const lastTriggerRef = useRef<HTMLButtonElement | null>(null);
  const isNarrowViewportRef = useRef(isNarrowViewport);
  isNarrowViewportRef.current = isNarrowViewport;
  const prevColourFilterRef = useRef<string | null | undefined>(undefined);

  const resolvedImages = images ?? EMPTY_IMAGES;

  const galleryImages = useMemo(
    () => buildPdpImageGalleryOrder(resolvedImages, colourFilterName),
    [resolvedImages, colourFilterName]
  );

  // When the active colour has no images of its own but borrowed ALTs were returned, show
  // a placeholder tile in the hero slot so the user knows the remaining images are not
  // colour-specific. The placeholder is rendered as a non-interactive leading cell in the
  // gallery grid; it is not included in the zoom modal image list.
  const colourHasNoImages = useMemo(() => {
    if (!colourFilterName) return false;
    return !resolvedImages.some((img) => img.swatchMatch === colourFilterName);
  }, [resolvedImages, colourFilterName]);

  const showBorrowedPlaceholder = colourHasNoImages && galleryImages.length > 0;

  const totalImages = galleryImages.length;
  const exceedsInitialCount = totalImages > initialImageCount;
  const shouldShowExpandButton = !isNarrowViewport && exceedsInitialCount;
  const displayedImages =
    isNarrowViewport || isExpanded || !exceedsInitialCount
      ? galleryImages
      : galleryImages.slice(0, initialImageCount);
  const {
    carouselRef,
    scrollbarRef,
    showScrollbar,
    scrollbarThumbStyle,
    onGalleryPointerDown,
    onGalleryClickCapture,
    onScrollbarPointerDown,
  } = useMobileCarousel(displayedImages.length);

  useEffect(() => {
    setCoverRangeOverride(getGalleryCoverRangeOverride());
  }, []);

  useEffect(() => {
    const prev = prevColourFilterRef.current;
    if (prev === undefined) {
      prevColourFilterRef.current = colourFilterName;
      return;
    }
    if (prev === colourFilterName) {
      return;
    }
    prevColourFilterRef.current = colourFilterName;

    if (isNarrowViewportRef.current) {
      carouselRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carouselRef stable; effect intentionally keyed to colour only
  }, [colourFilterName]);

  const openZoom = useCallback(
    (
      index: number,
      options?: {
        trigger?: HTMLButtonElement | null;
        source: 'keyboard' | 'mouse' | 'touch';
      }
    ) => {
      lastTriggerRef.current = options?.trigger ?? null;
      setZoomState({ isVisible: true, slideNum: index });
    },
    []
  );

  const closeZoom = useCallback(() => {
    setZoomState((state) => ({ ...state, isVisible: false }));
    window.requestAnimationFrame(() => {
      lastTriggerRef.current?.focus();
    });
  }, []);

  // Show placeholder when no images available
  if (galleryImages.length === 0) {
    return (
      <div className={styles.galleryRoot}>
        <div className={styles.galleryGrid}>
          <PlaceholderImage
            isPriority={true}
            sizes={FIRST_IMAGE_SIZES}
            imageFitMode={imageFitMode}
          >
            {(isMadeInCanada || isCanadianBrand) && (
              <CanadianBadge
                className={styles.canadianBadge}
                variant={isMadeInCanada ? 'madeInCanada' : 'canadianBrand'}
              />
            )}
          </PlaceholderImage>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.galleryRoot}>
      <GalleryTrack
        images={displayedImages}
        colourFilterName={colourFilterName}
        isMadeInCanada={isMadeInCanada}
        isCanadianBrand={isCanadianBrand}
        isNarrowViewport={isNarrowViewport}
        imageFitMode={imageFitMode}
        coverRangeOverride={coverRangeOverride}
        carouselRef={carouselRef}
        onPointerDown={onGalleryPointerDown}
        onClickCapture={onGalleryClickCapture}
        onImageActivate={openZoom}
        leadingNode={
          showBorrowedPlaceholder ? (
            <PlaceholderImage
              isPriority={true}
              sizes={FIRST_IMAGE_SIZES}
              imageFitMode={imageFitMode}
            >
              {(isMadeInCanada || isCanadianBrand) && (
                <CanadianBadge
                  className={styles.canadianBadge}
                  variant={isMadeInCanada ? 'madeInCanada' : 'canadianBrand'}
                />
              )}
            </PlaceholderImage>
          ) : undefined
        }
      />
      {showScrollbar && (
        <MobileScrollbar
          scrollbarRef={scrollbarRef}
          thumbStyle={scrollbarThumbStyle}
          onPointerDown={onScrollbarPointerDown}
        />
      )}
      {shouldShowExpandButton && (
        <div className={styles.expandButtonWrapper}>
          <ExpandButton
            expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded
              ? t('product.viewLess')
              : t('product.viewAllImages', { count: totalImages })}
          </ExpandButton>
        </div>
      )}
      {zoomState.isVisible && (
        <ZoomModal
          images={galleryImages}
          colourFilterName={colourFilterName}
          startingSlide={zoomState.slideNum}
          onClose={closeZoom}
        />
      )}
    </div>
  );
};

export default ImageGallery;
